import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { DragDropContext, Droppable, Draggable } from '../../../../third-party/react-beautiful-dnd.min';
import ActivityInstructionDialog from '../common/instructions-dialog/ActivityInstructionDialog';
import { useOpenInstructionDialog } from '../common/instructions-dialog/ActivityInstructionHooks';
import {
  useInitializeData,
  useGenerateComponentIds,
  useDragAndDropSubmitAnswer,
  useClickSubmitAnswer,
  useGenerateRejectedAnswerRefs,
  useSwitchAccessSubmitAnswer,
  usePrepareViewCorrectSubmissions,
} from './SortingActivityHooks';
import SortingActivityPrompt from './components/SortingActivityPrompt';
import SortingActivityResponse from './components/SortingActivityResponse';
import SelectedActivityResponseCards from './components/SelectedActivityResponseCards';
import RejectedAnswerResponse from '../common/rejected-answer-response/RejectedAnswerResponse';
import '../activities-common.scss';
import './SortingActivity.scss';
import ObjectUtils from '../../../../utils/ObjectUtils';
import LessonPlayActivityUtils from '../utils/LessonPlayActivityUtils';
import { filterUndefined } from '../../../../utils/FilterUndefinedObject';
import { useLessonPlay } from '../../context/LessonPlayContextProvider';

const defaultProps = {
  instructions: {},
  promptCards: [],
  responseCards: [],
  onCorrectAnswerSelected: () => { },
  onIncorrectAnswerSelected: () => { },
  onActivityFinished: () => { },
  isCurrentPage: false,
  onInstructionFinished: () => { },
  onTriggerSwitchScan: () => { },
};
const SortingActivity = forwardRef((props, ref) => {
  const mergedProps = { ...defaultProps, ...filterUndefined(props) };
  const { lessonPlayDomain } = useLessonPlay();
  const { userProfile } = lessonPlayDomain.domainData;
  const { switchAccessibility } = userProfile;
  const {
    instructions,
    promptCards,
    responseCards,
    onCorrectAnswerSelected,
    onIncorrectAnswerSelected,
    onActivityFinished,
    isCurrentPage,
    onInstructionFinished,
    onTriggerSwitchScan,
    pageIndex,
  } = mergedProps;
  const {
    sortingActivityRef,
    dragAndDropApiRef,
  } = useInitializeData();
  const disabledFocusElementsRef = useRef([]);

  const {
    id,
    componentId,
    droppableId,
    textLineId,
  } = useGenerateComponentIds();

  const {
    openInstructions,
    handleOnInstructionClosed,
    audioPlaying,
    hasOpen,
  } = useOpenInstructionDialog({ instructions, isCurrentPage, textLineId, onInstructionFinished });

  const {
    rejectedAnswerPromptRefs,
    setRejectedAnswerPromptRefs,
    rejectedAnswerResponseRefs,
    setRejectedAnswerResponseRefs,
  } = useGenerateRejectedAnswerRefs();

  const {
    handleOnDragStart,
    handleOnDragEnd,
    selectedResponseCards,
    isFinished,
    setSelectedResponseCards,
  } = useDragAndDropSubmitAnswer({
    sortingActivityRef,
    promptCards,
    rejectedAnswerPromptRefs,
    responseCards,
    rejectedAnswerResponseRefs,
    onCorrectAnswerSelected,
    onIncorrectAnswerSelected,
    onActivityFinished,
  });
  const selectedResponseCardsRef = useRef(selectedResponseCards);

  const {
    handleOnPromptCardClick,
    handleOnResponseCardClick,
  } = useClickSubmitAnswer({
    sortingActivityRef,
    dragAndDropApiRef,
  });

  usePrepareViewCorrectSubmissions({
    setSelectedResponseCards,
    responseCards,
    isFinished,
    onActivityFinished,
    pageIndex,
  });

  useEffect(() => {
    selectedResponseCardsRef.current = selectedResponseCards;
  }, [selectedResponseCards]);

  const sortingPromptRefs = [];
  const setSortingPromptRefs = (r, index) => {
    if (r) {
      sortingPromptRefs[index] = r;
    }
  };

  const sortingResponseRefs = [];
  const setSortingResponseRefs = (r, index) => {
    if (r) {
      sortingResponseRefs[index] = r;
    }
  };

  const {
    selectedPromptIndex,
    handlePromptSwitchAccessSelect,
    handleResponseSwitchAccessSelect,
  } = useSwitchAccessSubmitAnswer({
    sortingPromptRefs,
    sortingResponseRefs,
    onTriggerSwitchScan,
    dragAndDropApiRef,
  });

  // When the instruction dialog mounts, refresh scan/tab targets so the body list matches
  // `getEnableElements` (instruction-only while open). Otherwise the list stays stale from the last scan.
  useEffect(() => {
    if (!openInstructions.open || !onTriggerSwitchScan) {
      return undefined;
    }
    const timer = ObjectUtils.setTimeout(() => {
      onTriggerSwitchScan();
    }, 200);
    return () => clearTimeout(timer);
  }, [openInstructions.open, onTriggerSwitchScan]);

  useEffect(() => {
    const focusableSelector = [
      'button',
      '[href]',
      'input',
      'select',
      'textarea',
      '[tabindex]',
    ].join(', ');

    if (openInstructions.open) {
      const root = sortingActivityRef.current;
      if (!root) {
        return undefined;
      }

      const focusables = root.querySelectorAll(focusableSelector);
      disabledFocusElementsRef.current = [];
      focusables.forEach((el) => {
        disabledFocusElementsRef.current.push({
          element: el,
          tabindex: el.getAttribute('tabindex'),
        });
        el.setAttribute('tabindex', '-1');
      });

      return undefined;
    }

    disabledFocusElementsRef.current.forEach(({ element, tabindex }) => {
      if (!element || !element.setAttribute || !element.removeAttribute) {
        return;
      }
      if (tabindex === null || tabindex === undefined) {
        element.removeAttribute('tabindex');
      } else {
        element.setAttribute('tabindex', tabindex);
      }
    });
    disabledFocusElementsRef.current = [];

    return undefined;
  }, [openInstructions.open, sortingActivityRef]);

  useImperativeHandle(ref, () => ({
    // While the instruction dialog is open, skip the top bar so scan/tab targets only the instruction UI.
    skipAppBar: () => openInstructions.open
      || (switchAccessibility && selectedPromptIndex.current === null),
    getEnableElements: async () => {
      if (isFinished.current) {
        return [];
      }

      // `hasOpen` ref is set true only after the instruction closes; while the dialog is visible,
      // expose the instruction surface so Tab/scan focus stays on instructions (not the activity).
      if (openInstructions.open) {
        await ObjectUtils.delay(100);
        const instructionDialog = document.querySelector('[data-test="activity-instruction-dialog"]');
        if (instructionDialog) {
          const closeButton = instructionDialog.querySelector('.close-button');
          return [closeButton || instructionDialog];
        }
        return [];
      }

      if (!hasOpen.current) {
        return [];
      }

      if (!switchAccessibility) {
        await ObjectUtils.delay(50);
        const unselectedResponseElements = sortingResponseRefs
          .map((s, index) => (s && !selectedResponseCardsRef.current.includes(index) ? s.getEnableElements() : null))
          .filter(Boolean);
        const promptDropElements = sortingPromptRefs
          .map((s) => s.getEnableElements())
          .filter(Boolean);
        return [...promptDropElements, ...unselectedResponseElements];
      }

      if (selectedPromptIndex.current === null) {
        return sortingPromptRefs.map((s) => s.getEnableElements());
      }
      await ObjectUtils.delay(50);
      const remainPrompts = sortingPromptRefs
        .filter((_s, index) => index !== selectedPromptIndex.current.index)
        .map((s) => s.getEnableElements());
      const remainingResponse = sortingResponseRefs
        .filter((_s, index) => !selectedResponseCardsRef.current.includes(index))
        .map((s) => s.getEnableElements());

      return [
        ...remainingResponse,
        ...remainPrompts,
      ];
    },
  }));

  return (
    <>
      <ActivityInstructionDialog
        id={textLineId}
        open={openInstructions.open && !isFinished.current}
        instructions={instructions}
        onClose={handleOnInstructionClosed}
        audioPlaying={audioPlaying}
      />
      <DragDropContext
        onDragStart={handleOnDragStart}
        onDragEnd={handleOnDragEnd}
        sensors={[(sensorApi) => {
          dragAndDropApiRef.current = sensorApi;
        }]}
      >
        <div className='sorting-activity-component' data-test='sorting-activity-component' ref={sortingActivityRef} id={componentId}>
          <Droppable
            droppableId={droppableId}
            direction='horizontal'
            isDropDisabled
            isCombineEnabled
          >
            {(sortingProvided) => (
              <div
                ref={sortingProvided.innerRef}
                {...sortingProvided.droppableProps}
                className='sorting-prompt-draggable'
              >
                <div className='sorting-prompt-area' data-test='sorting-prompt-area'>
                  {promptCards.map((promptCard, index) => {
                    const promptId = `sorting-prompt-${id}-${index}`;
                    const drappableKeyId = `sorting-component-droppable-container-${id}-${index}`;
                    const promptPlaceholderId = `sorting-prompt-placeholder-${id}-${index}`;
                    return (
                      <Droppable
                        key={drappableKeyId}
                        droppableId={drappableKeyId}
                        direction='horizontal'
                        index={index + 1}
                        data-test={`sorting-prompt-area-${index}`}
                      >
                        {(provided, snapshot) => (
                          <SortingActivityPrompt
                            promptCardId={promptId}
                            promptCard={promptCard}
                            isRightAlignLayout={index % 2 === 1}
                            switchAccessibility={switchAccessibility}
                            onResponseCardsAreaClick={(e) => {
                              handleOnPromptCardClick(e, promptPlaceholderId);
                            }}
                            ref={(r) => { setSortingPromptRefs(r, index); }}
                            onPromptSwitchAccessSelected={() => {
                              if (switchAccessibility) {
                                handlePromptSwitchAccessSelect(index, promptPlaceholderId);
                              } else {
                                handleOnPromptCardClick({ preventDefault: () => {} }, promptPlaceholderId);
                              }
                            }}
                          >
                            <div
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                              id={promptPlaceholderId}
                              className={`
                                selected-response-cards 
                                ${snapshot.isDraggingOver ? 'sorting-selected-component' : 'sorting-component-response-card-non-drag-items'}
                                `}
                              data-test={`sorting-drop-zone-${index}`}
                            >
                              <SelectedActivityResponseCards
                                id={id}
                                promptKey={promptCard.key}
                                selectedResponseCards={selectedResponseCards}
                                responseCards={responseCards}
                              />

                              <RejectedAnswerResponse ref={setRejectedAnswerPromptRefs} showIcon={false} />
                            </div>
                            {provided.placeholder}
                          </SortingActivityPrompt>
                        )}
                      </Droppable>
                    );
                  })}
                </div>
                <div className='sorting-response-area-container'>
                  <div className='sorting-response-cards'>
                    {
                      responseCards.map((responseCard, index) => {
                        const cardId = `${id}-${index}`;
                        const responseKeyId = `sorting-response-key-${cardId}`;
                        const responseId = `sorting-response-${cardId}`;
                        const draggableId = `sorting-drag-item-${cardId}`;
                        const isResponseSelected = selectedResponseCards.includes(index);
                        return (
                          <div key={responseKeyId} id={draggableId} className={`response-card-place-holder item-placeholder response-margin-${responseCards.length}-responses`}>
                            <Draggable
                              key={draggableId}
                              draggableId={draggableId}
                              index={index}
                              isDragDisabled={selectedResponseCards.includes(index)}
                              shouldRespectForcePress={false}
                              timeForLongPress={0}
                            >
                              {(providedItem, snapshotItem) => (
                                <div
                                  ref={providedItem.innerRef}
                                  {...providedItem.draggableProps}
                                  {...providedItem.dragHandleProps}
                                  id={`sorting-response-item-${cardId}`}
                                  key={`sorting-response-item-${cardId}`}
                                  className={`${isResponseSelected ? '' : 'sorting-response-draggable'} 
                                    ${snapshotItem.isDragging ? 'sorting-selected-component' : 'non-drag-items'}
                                    `}
                                  style={LessonPlayActivityUtils.getDropStyle(providedItem.draggableProps.style, snapshotItem)}
                                  data-test={`sorting-answer-card-${index}`}
                                  aria-label={responseCard.name || 'response card'}
                                >
                                  {
                                    !isResponseSelected && (
                                      <>
                                        <SortingActivityResponse
                                          responseCard={responseCard}
                                          responseCardId={responseId}
                                          onClick={(e, isTtsAbleToPlay) => {
                                            handleOnResponseCardClick(e, isTtsAbleToPlay, responseCard, responseId, draggableId);
                                          }}
                                          ref={(r) => { setSortingResponseRefs(r, index); }}
                                          onResponseSwitchAccessSelected={() => {
                                            if (switchAccessibility) {
                                              handleResponseSwitchAccessSelect(responseId, draggableId);
                                            } else {
                                              handleOnResponseCardClick({}, false, responseCard, responseId, draggableId);
                                            }
                                          }}
                                        />
                                        <RejectedAnswerResponse ref={(r) => { setRejectedAnswerResponseRefs(r, index); }} />
                                      </>
                                    )
                                  }
                                </div>
                              )}
                            </Draggable>
                          </div>
                        );
                      })
                    }
                  </div>
                </div>
                {sortingProvided.placeholder}
              </div>
            )}
          </Droppable>
        </div>
      </DragDropContext>
    </>
  );
});

export default SortingActivity;
